"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const meta_pop_json_1 = __importDefault(require("../../assets/spritesheets/meta_pop.json"));
const anim_pop_png_1 = __importDefault(require("../../assets/img/anim_pop.png"));
const pixi_js_1 = require("pixi.js");
const samples_json_1 = __importDefault(require("../../assets/drawings/samples.json"));
const tick_wav_1 = __importDefault(require("../../assets/sounds/tick.wav"));
const untick_wav_1 = __importDefault(require("../../assets/sounds/untick.wav"));
const squish_wav_1 = __importDefault(require("../../assets/sounds/squish.wav"));
const erase_wav_1 = __importDefault(require("../../assets/sounds/erase.wav"));
const slide_wav_1 = __importDefault(require("../../assets/sounds/slide.wav"));
const ding_wav_1 = __importDefault(require("../../assets/sounds/ding.wav"));
const thud_wav_1 = __importDefault(require("../../assets/sounds/thud.wav"));
const stick_wav_1 = __importDefault(require("../../assets/sounds/stick.wav"));
const woosh_wav_1 = __importDefault(require("../../assets/sounds/woosh.wav"));
const pop_wav_1 = __importDefault(require("../../assets/sounds/pop.wav"));
const sb_lifeinsilico_mp3_1 = __importDefault(require("../../assets/sounds/sb_lifeinsilico.mp3"));
const streak_svg_1 = __importDefault(require("../../assets/svg/streak.svg"));
const home_fill_svg_1 = __importDefault(require("../../assets/svg/home_fill.svg"));
const palette_svg_1 = __importDefault(require("../../assets/svg/palette.svg"));
const podium_svg_1 = __importDefault(require("../../assets/svg/podium.svg"));
const brushpoint_svg_1 = __importDefault(require("../../assets/svg/brushpoint.svg"));
const spash_svg_1 = __importDefault(require("../../assets/svg/spash.svg"));
const arrow_right_svg_1 = __importDefault(require("../../assets/svg/arrow_right.svg"));
const arrow_simple_svg_1 = __importDefault(require("../../assets/svg/arrow_simple.svg"));
const spash_simple_svg_1 = __importDefault(require("../../assets/svg/spash_simple.svg"));
const cog_svg_1 = __importDefault(require("../../assets/svg/cog.svg"));
const Global_1 = require("../Global");
const settings_1 = require("../config/settings");
const AudioContext = window.AudioContext || window.webkitAudioContext; // Safari and old versions of Chrome
class AssetManager {
    constructor() {
        this.soundsOn = true;
        this.bgplay = false;
        // Assets to load before game is started, all others will be loaded async
        this.preload = { fire: streak_svg_1.default, brushpoint: brushpoint_svg_1.default, home: home_fill_svg_1.default, palette: palette_svg_1.default, podium: podium_svg_1.default, splash: spash_svg_1.default,
            arrow_simple: arrow_simple_svg_1.default, arrow_right: arrow_right_svg_1.default, splashsimple: spash_simple_svg_1.default, cog: cog_svg_1.default };
        this.sounds = {
            woosh: { sound: woosh_wav_1.default, volume: 0.15 },
            tick: { sound: tick_wav_1.default, volume: 0.5 },
            untick: { sound: untick_wav_1.default, volume: 0.5 },
            squish: { sound: squish_wav_1.default, volume: 0.3 },
            slide: { sound: slide_wav_1.default, volume: 0.5 },
            erase: { sound: erase_wav_1.default, volume: 0.4 },
            stick: { sound: stick_wav_1.default, volume: 0.2 },
            thud: { sound: thud_wav_1.default, volume: 0.3 },
            ding: { sound: ding_wav_1.default, volume: 0.4 },
            pop: { sound: pop_wav_1.default, volume: 0.22 }
        };
        if (AudioContext) {
            this.audioContext = new AudioContext();
        }
    }
    refreshAudio() {
        var _a;
        // So some stupid browsers like Safari have really dropped the ball on implementing the Audio APIs
        // We need to play a "dummy" audio clip of silence to trick Safari on iOS into thinking we have HTML Audio...
        // It's crazy how complex it is just to play simple audio on web
        clearInterval(this.dummycheck);
        (_a = this.dummyaudio) === null || _a === void 0 ? void 0 : _a.remove();
        this.dummyaudio = null;
        this.dummycheck = setInterval(() => __awaiter(this, void 0, void 0, function* () {
            var _b, _c;
            if (((_b = this.audioContext) === null || _b === void 0 ? void 0 : _b.state) === 'running') {
                clearInterval(this.dummycheck);
                yield this.audioContext.resume();
                (_c = this.dummyaudio) === null || _c === void 0 ? void 0 : _c.remove();
                this.dummyaudio = new Audio();
                this.dummyaudio.autoplay = true;
                this.dummyaudio.src = "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
            }
        }), 100);
    }
    // Maybe make this lazy load each in the future if it takes too long
    loadAll() {
        return __awaiter(this, arguments, void 0, function* () {
            // Load needed assets
            // TODO: Clean this up, we don't really need to store them here, they are already cached inside Pixi!
            for (let key in this.preload) {
                try {
                    this.preload[key] = yield pixi_js_1.Assets.load(this.preload[key]);
                }
                catch (e) {
                    console.log(arguments, e);
                    // Problem with loading asset
                }
            }
            // TODO: Fix this for iOS raw loader
            this.popSheet = new pixi_js_1.Spritesheet(pixi_js_1.Texture.from(anim_pop_png_1.default), meta_pop_json_1.default);
            yield this.popSheet.parse();
            // Load audio
            if (this.audioContext) {
                this.refreshAudio();
                this.backgroundGain = this.audioContext.createGain();
                this.backgroundGain.gain.value = settings_1.Settings.loudMusicVolume;
                // Fetch in the background to not block the game (the music file is 3MB)
                fetch(sb_lifeinsilico_mp3_1.default).then((response) => __awaiter(this, void 0, void 0, function* () {
                    const arrayBuffer = yield response.arrayBuffer();
                    this.bgbuffer = yield this.audioContext.decodeAudioData(arrayBuffer);
                })).catch((e) => console.warn(e));
                for (let s in this.sounds) {
                    try {
                        const response = yield fetch(this.sounds[s].sound);
                        const arrayBuffer = yield response.arrayBuffer();
                        this.sounds[s].buffer = yield this.audioContext.decodeAudioData(arrayBuffer);
                    }
                    catch (e) {
                        console.warn(e);
                    }
                }
            }
        });
    }
    tryPlayBackground() {
        // We have to keep checking until a user makes a gesture on the page to start the audio
        clearInterval(this.bgcheck);
        this.bgcheck = setInterval(() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                if (this.dummyaudio && this.bgbuffer) {
                    clearInterval(this.bgcheck);
                    (_a = this.background) === null || _a === void 0 ? void 0 : _a.disconnect();
                    this.background = this.audioContext.createBufferSource();
                    this.background.loop = true;
                    this.background.buffer = this.bgbuffer;
                    this.background.connect(this.backgroundGain);
                    this.backgroundGain.connect(this.audioContext.destination);
                    this.background.start();
                }
            }
            catch (e) {
                console.warn(e);
            }
        }), 100);
    }
    masterSound(ison) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.audioContext) {
                if (ison) {
                    if (this.audioContext.state !== 'running') {
                        yield this.audioContext.resume();
                        this.refreshAudio();
                    }
                }
                else {
                    if (this.audioContext.state === 'running') {
                        yield this.audioContext.suspend();
                    }
                }
            }
        });
    }
    get backgroundOn() {
        return this.bgplay;
    }
    set backgroundOn(is) {
        var _a;
        if (is && !this.bgplay) {
            this.bgplay = is;
            this.tryPlayBackground();
        }
        else if (!is && this.bgplay) {
            this.bgplay = is;
            (_a = this.background) === null || _a === void 0 ? void 0 : _a.stop();
        }
    }
    get brushpoint() {
        return this.preload.brushpoint;
    }
    get fire() {
        return this.preload.fire;
    }
    popSound() {
        return this.playSound('pop');
    }
    dingSound() {
        return this.playSound('ding');
    }
    thudSound() {
        return this.playSound('thud');
    }
    eraseSound() {
        return this.playSound('erase');
    }
    stickSound() {
        return this.playSound('stick');
    }
    slideSound() {
        return this.playSound('slide');
    }
    wooshSound() {
        return this.playSound('woosh');
    }
    untickSound() {
        return this.playSound('untick');
    }
    tickSound() {
        return this.playSound('tick');
    }
    squishSound() {
        return this.playSound('squish');
    }
    playSound(key) {
        var _a;
        if (this.audioContext && key && ((_a = this.sounds[key]) === null || _a === void 0 ? void 0 : _a.buffer) && this.soundsOn) {
            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = this.sounds[key].volume;
            source.buffer = this.sounds[key].buffer;
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
            source.onended = () => {
                source.disconnect();
                gainNode.disconnect();
            };
            source.start(0);
        }
        return null;
    }
    popSprite() {
        const anim = new pixi_js_1.AnimatedSprite(this.popSheet.animations['pop']);
        anim.animationSpeed = 0.25;
        anim.scale.set((0, Global_1.px)(0.4));
        anim.anchor.set(0.5, 0.5);
        return anim;
    }
    randomStrokes() {
        const packs = [samples_json_1.default]; // Register new packs of drawings here
        const pack = packs[Math.floor(Math.random() * packs.length)];
        const drawing = pack[Math.floor(Math.random() * pack.length)];
        return drawing.drawing;
    }
}
const assetManager = new AssetManager();
exports.default = assetManager;
